/**
 * @license
 *-------------------------------------------------------------------------------------------
 * Copyright © 2024 Progress Software Corporation. All rights reserved.
 * Licensed under commercial license. See LICENSE.md in the package root for more information
 *-------------------------------------------------------------------------------------------
 */
"use client";
import * as o from "react";
import r from "prop-types";
import { StepperContext as ee } from "./context/StepperContext.mjs";
import { validatePackage as te, focusFirstFocusableChild as se, useRtl as oe, dispatchEvent as A, Keys as u, classNames as F, svgIconPropType as M } from "@progress/kendo-react-common";
import { Step as ae } from "./Step.mjs";
import { ProgressBar as re } from "@progress/kendo-react-progressbars";
import { DEFAULT_ANIMATION_DURATION as ne, NO_ANIMATION as ie } from "./contants.mjs";
import { packageMetadata as le } from "../package-metadata.mjs";
import { useLocalization as ce } from "@progress/kendo-react-intl";
import { progBarAriaLabel as O, messages as ue } from "./messages/index.mjs";
const E = o.forwardRef((k, V) => {
  te(le);
  const {
    animationDuration: b,
    children: L,
    className: D,
    disabled: p,
    errorIcon: z,
    errorSVGIcon: G,
    item: N,
    items: d,
    linear: m,
    mode: K,
    orientation: y,
    style: R,
    successIcon: _,
    successSVGIcon: B,
    onChange: h,
    onFocus: C
  } = k, H = ce().toLanguageString(O, ue[O]), g = o.useRef(null), w = o.useCallback(
    () => {
      g.current && se(g.current);
    },
    []
  ), I = o.useCallback(
    () => ({
      element: g.current,
      focus: w
    }),
    [w]
  );
  o.useImperativeHandle(V, I);
  const i = k.value || P.value, [f, n] = o.useState(i), c = d ? d.length : 0, a = y === "vertical", S = oe(g, k.dir), U = typeof b == "number" ? b : b !== !1 ? ne : ie;
  o.useEffect(() => {
    n(i);
  }, [i]);
  const v = o.useCallback(
    (t, s) => {
      const e = s === i - 1, l = s === i, $ = s === i + 1;
      i !== s && h && !p && (!m || e || l || $) && (A(
        h,
        t,
        I(),
        { value: s }
      ), n(s));
    },
    [i, m, h, p, n]
  ), j = o.useCallback(
    (t) => {
      let s = t.value, e = t.syntheticEvent;
      v(e, s);
    },
    [v]
  ), q = o.useCallback(
    (t) => {
      C && !p && A(
        C,
        t.syntheticEvent,
        I(),
        void 0
      );
    },
    [C, p]
  ), x = o.useCallback(
    (t) => {
      v(t, f);
    },
    [v, f]
  ), J = o.useCallback(
    (t) => {
      const s = S === "rtl", e = f, l = d.length - 1;
      switch (t.keyCode) {
        case u.left:
          t.preventDefault(), !s && e > 0 && n(e - 1), s && e < l && n(e + 1);
          break;
        case u.right:
          t.preventDefault(), !s && e < l && n(e + 1), s && e > 0 && n(e - 1);
          break;
        case u.up:
          t.preventDefault(), !s && e > 0 && n(e - 1), s && e > 0 && n(e - 1);
          break;
        case u.down:
          t.preventDefault(), !s && e < l && n(e + 1), s && e < l && n(e + 1);
          break;
        case u.home:
          t.preventDefault(), n(0);
          break;
        case u.end:
          t.preventDefault(), n(l);
          break;
        case u.space:
          t.preventDefault();
          break;
        case u.enter:
          t.preventDefault(), d[e].disabled || x(t);
          break;
      }
    },
    [d, n, f, S, x]
  ), Q = o.useMemo(
    () => F(
      "k-stepper",
      {
        "k-stepper-linear": m
      },
      D
    ),
    [m, D]
  ), W = o.useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: a ? void 0 : "repeat(" + c * 2 + ", 1fr)",
      gridTemplateRows: a ? "repeat(" + c + ", 1fr)" : void 0,
      ...R
    }),
    [a, c, R]
  ), X = o.useMemo(
    () => F(
      "k-step-list",
      {
        "k-step-list-horizontal": !a,
        "k-step-list-vertical": a
      }
    ),
    [a]
  ), Y = o.useMemo(
    () => ({
      gridColumnStart: a ? void 0 : 1,
      gridColumnEnd: a ? void 0 : -1,
      gridRowStart: a ? 1 : void 0,
      gridRowEnd: a ? -1 : void 0
    }),
    [a]
  ), Z = o.useMemo(
    () => ({
      gridColumnStart: a ? void 0 : 2,
      gridColumnEnd: a ? void 0 : c * 2,
      gridRowStart: a ? 1 : void 0,
      gridRowEnd: a ? c : void 0,
      top: a ? 17 : void 0
    }),
    [a, c]
  ), T = d && d.map((t, s) => {
    const e = {
      index: s,
      disabled: p || t.disabled,
      focused: s === f,
      current: s === i,
      ...t
    }, l = N || ae;
    return /* @__PURE__ */ o.createElement(l, { key: s, ...e });
  });
  return /* @__PURE__ */ o.createElement(
    ee.Provider,
    {
      value: {
        animationDuration: b,
        isVertical: a,
        item: N,
        linear: m,
        mode: K,
        numOfSteps: c,
        value: i,
        successIcon: _,
        successSVGIcon: B,
        errorIcon: z,
        errorSVGIcon: G,
        onChange: j,
        onFocus: q
      }
    },
    /* @__PURE__ */ o.createElement(
      "nav",
      {
        className: Q,
        style: W,
        dir: S,
        onKeyDown: J
      },
      /* @__PURE__ */ o.createElement(
        "ol",
        {
          className: X,
          style: Y
        },
        T || L
      ),
      /* @__PURE__ */ o.createElement(
        re,
        {
          style: Z,
          animation: { duration: U },
          ariaLabel: H,
          "aria-hidden": !0,
          max: c - 1,
          labelVisible: !1,
          orientation: y,
          reverse: y === "vertical",
          value: i,
          disabled: p,
          tabIndex: -1
        }
      )
    )
  );
});
E.propTypes = {
  animationDuration: r.oneOfType([r.bool, r.number]),
  children: r.any,
  className: r.string,
  dir: r.string,
  disabled: r.bool,
  errorIcon: r.string,
  errorSVGIcon: M,
  item: r.any,
  items: r.any,
  linear: r.bool,
  mode: r.oneOf(["steps", "labels"]),
  orientation: r.oneOf(["horizontal", "vertical"]),
  style: r.object,
  successIcon: r.string,
  successSVGIcon: M,
  value: r.number.isRequired,
  onChange: r.func,
  onFocus: r.func
};
const P = {
  value: 0
};
E.defaultProps = P;
E.displayName = "KendoStepper";
export {
  E as Stepper
};
